import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./PaymentPage.css";
import logo from "../../Assets/ritu.png";
import { toast, ToastContainer } from "react-toastify";

import {
    ShieldCheck,
    Wifi,
    User,
    Mail,
    Phone,
    Lock,
    ArrowLeft,
    CreditCard,
    FileText,
    Tv
} from "lucide-react";

function PaymentPage() {

    const location = useLocation();
    const navigate = useNavigate();

    /*
    =========================================================
    INDIA TIMEZONE HELPERS
    =========================================================

    The PHP/MySQL backend returns DATETIME values such as:

        2026-08-15 12:43:18

    These values do not contain timezone information. Do not pass
    them directly to new Date(), because the browser may interpret
    them differently depending on its timezone.

    Treat timezone-less backend DATETIME values as Asia/Kolkata.
    =========================================================
    */

    const INDIA_TIMEZONE = "Asia/Kolkata";
    const INDIA_OFFSET_MINUTES = 330;

    const parseIndiaDateTime = (value) => {

        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime())
                ? null
                : value;
        }

        const raw = String(value).trim();

        if (!raw) {
            return null;
        }

        /*
        If the backend ever returns an explicit timezone,
        respect that timezone.

        Examples:
            2026-08-15T12:43:18+05:30
            2026-08-15T07:13:18Z
        */
        if (
            /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw)
        ) {

            const explicitDate =
                new Date(raw);

            return Number.isNaN(
                explicitDate.getTime()
            )
                ? null
                : explicitDate;
        }

        /*
        MySQL DATETIME:

            YYYY-MM-DD HH:mm:ss

        or:

            YYYY-MM-DDTHH:mm:ss
        */
        const match = raw.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
        );

        if (match) {

            const year =
                Number(match[1]);

            const month =
                Number(match[2]);

            const day =
                Number(match[3]);

            const hour =
                Number(match[4] || 0);

            const minute =
                Number(match[5] || 0);

            const second =
                Number(match[6] || 0);

            const milliseconds =
                Number(
                    (match[7] || "0")
                        .padEnd(3, "0")
                        .slice(0, 3)
                );

            /*
            Build the instant represented by the
            Asia/Kolkata wall-clock value.
            */
            const utcMillis =
                Date.UTC(
                    year,
                    month - 1,
                    day,
                    hour,
                    minute,
                    second,
                    milliseconds
                ) -
                INDIA_OFFSET_MINUTES * 60 * 1000;

            const parsedDate =
                new Date(utcMillis);

            return Number.isNaN(
                parsedDate.getTime()
            )
                ? null
                : parsedDate;
        }

        /*
        Fallback for other valid date formats.
        */
        const fallbackDate =
            new Date(raw);

        return Number.isNaN(
            fallbackDate.getTime()
        )
            ? null
            : fallbackDate;
    };

    /*
    =========================================================
    SESSION USER DETAILS
    =========================================================
    */

    const username =
        sessionStorage.getItem("username") || "";

    const customerName =
        sessionStorage.getItem("fullname") || "";

    const email =
        sessionStorage.getItem("email") || "";

    const mobile =
        sessionStorage.getItem("mobile") || "";


    /*
    =========================================================
    DATA RECEIVED FROM USER PAGE
    =========================================================

    Expected navigation:

    Broadband only:

    navigate("/payment", {
        state: {
            plan,
            duration,
            planType: "BROADBAND",
            selectedAddon: null
        }
    });

    Broadband + Add-on:

    navigate("/payment", {
        state: {
            plan,
            duration,
            planType: "BROADBAND",
            selectedAddon: addon
        }
    });

    Direct Add-on:

    navigate("/payment", {
        state: {
            plan: addon,
            duration: addon,
            planType: "ADDON",
            selectedAddon: addon
        }
    });
    =========================================================
    */

    const state = location.state || {};

    const plan = state.plan || null;

    const duration =
        state.duration ||
        state.selectedDuration ||
        null;

    const rawPlanType =
        state.planType ||
        "BROADBAND";

    /*
    Normalize plan type.

    Old code may send:
        OTT_ADDON

    New backend expects:
        ADDON
    */

    const planType =
        String(rawPlanType).toUpperCase() === "OTT_ADDON"
            ? "ADDON"
            : String(rawPlanType).toUpperCase();


    const selectedAddon =
        state.selectedAddon ||
        state.addonPlan ||
        state.addon ||
        null;


    /*
    =========================================================
    DETERMINE PURCHASE TYPE
    =========================================================
    */

    const isDirectAddonPurchase =
        planType === "ADDON";

    const hasAddonWithBroadband =
        planType === "BROADBAND" &&
        !!selectedAddon;


    /*
    =========================================================
    STATE
    =========================================================
    */

    const [loading, setLoading] =
        useState(false);

    /*
    =========================================================
    ACTIVE BROADBAND PLAN STATE
    =========================================================

    We check the current broadband plan for BOTH:

        1. Broadband purchases
        2. OTT add-on purchases

    Reason:

    If the user already has an active broadband plan and tries
    to make another payment, we must show a confirmation before
    opening Razorpay.

    For a direct OTT add-on purchase, an active broadband plan is
    mandatory.
    =========================================================
    */

    const [activePlanChecking, setActivePlanChecking] =
        useState(state.hasActiveBroadband !== true);

    const [hasActiveBroadband, setHasActiveBroadband] =
        useState(state.hasActiveBroadband === true);

    const [activeBroadbandPlan, setActiveBroadbandPlan] =
        useState(null);

    const [showActivePlanConfirmation, setShowActivePlanConfirmation] =
        useState(false);


    /*
    =========================================================
    GET BROADBAND AMOUNT
    =========================================================
    */

    const broadbandAmount = useMemo(() => {

        if (isDirectAddonPurchase) {
            return 0;
        }

        if (!duration) {
            return 0;
        }

        return Number(
            duration.price ||
            plan?.price ||
            0
        );

    }, [
        duration,
        plan,
        isDirectAddonPurchase
    ]);


    /*
    =========================================================
    GET ADD-ON AMOUNT
    =========================================================
    */

    const addonAmount = useMemo(() => {

        if (!selectedAddon) {
            return 0;
        }

        /*
        For direct ADDON purchase:
            selectedAddon.price

        For broadband + addon:
            selectedAddon.price
        */

        return Number(
            selectedAddon.price ||
            selectedAddon.amount ||
            0
        );

    }, [selectedAddon]);


    /*
    =========================================================
    SERVICE SUBTOTAL
    =========================================================

    Broadband only:

        Broadband

    Broadband + Add-on:

        Broadband + Add-on

    Direct Add-on:

        Add-on
    =========================================================
    */

    const serviceSubtotal = useMemo(() => {

        if (isDirectAddonPurchase) {
            return addonAmount;
        }

        return (
            broadbandAmount +
            addonAmount
        );

    }, [
        isDirectAddonPurchase,
        broadbandAmount,
        addonAmount
    ]);


    /*
    =========================================================
    GST
    =========================================================

    18%
    =========================================================
    */

    const serviceGst = useMemo(() => {

        return 0;

    }, []);


    /*
    =========================================================
    RAZORPAY PLATFORM FEE
    =========================================================

    2%
    =========================================================
    */

    const razorpayFee = useMemo(() => {

        return Number(
            (
                serviceSubtotal * 0.02
            ).toFixed(2)
        );

    }, [serviceSubtotal]);


    /*
    =========================================================
    GST ON RAZORPAY FEE
    =========================================================

    18%
    =========================================================
    */

    const razorpayFeeGst = useMemo(() => {

        return Number(
            (
                razorpayFee * 0.18
            ).toFixed(2)
        );

    }, [razorpayFee]);


    /*
    =========================================================
    TOTAL PAYABLE
    =========================================================

    Service amount
        +
    Service GST
        +
    Razorpay fee
        +
    Razorpay fee GST
    =========================================================
    */

    const totalPayable = useMemo(() => {

        return Number(
            (
                serviceSubtotal +
                serviceGst +
                razorpayFee +
                razorpayFeeGst
            ).toFixed(2)
        );

    }, [
        serviceSubtotal,
        serviceGst,
        razorpayFee,
        razorpayFeeGst
    ]);


    /*
    =========================================================
    BROADBAND DURATION LABEL
    =========================================================
    */

    const broadbandDurationLabel = useMemo(() => {

        if (!duration) {
            return "";
        }

        return (
            duration.duration_label ||
            duration.label ||
            `${duration.months || duration.duration_months || 1} Month`
        );

    }, [duration]);


    /*
    =========================================================
    ADD-ON DURATION LABEL
    =========================================================
    */

    const addonDurationLabel = useMemo(() => {

        if (!selectedAddon) {
            return "";
        }

        return (
            selectedAddon.duration_label ||
            selectedAddon.label ||
            `${selectedAddon.months || selectedAddon.duration_months || 1} Months`
        );

    }, [selectedAddon]);


    /*
    =========================================================
    ADD-ON PLAN NAME
    =========================================================
    */

    const addonPlanName = useMemo(() => {

        if (!selectedAddon) {
            return "OTT Add-on";
        }

        return (
            selectedAddon.plan_name ||
            selectedAddon.addon_plan_name ||
            selectedAddon.name ||
            `OTT Add-on - ${addonDurationLabel}`
        );

    }, [
        selectedAddon,
        addonDurationLabel
    ]);


    /*
    =========================================================
    DISPLAY PLAN NAME
    =========================================================
    */

    const displayPlanName = useMemo(() => {

        if (isDirectAddonPurchase) {
            return addonPlanName;
        }

        return (
            plan?.plan_name ||
            plan?.name ||
            "Broadband Plan"
        );

    }, [
        isDirectAddonPurchase,
        addonPlanName,
        plan
    ]);


    /*
    =========================================================
    CHECK ACTIVE BROADBAND PLAN
    =========================================================

    Only needed for DIRECT ADD-ON purchase.
    =========================================================
    */

    useEffect(() => {

        /*
        If the User page already confirmed that an active
        broadband plan exists, use that information and avoid
        another API request.
        */
        if (state.hasActiveBroadband === true) {

            setHasActiveBroadband(true);
            setActivePlanChecking(false);

            if (state.activeBroadbandPlan) {
                setActiveBroadbandPlan(
                    state.activeBroadbandPlan
                );
            }

            return;
        }


        const checkActiveBroadband = async () => {

            if (!username) {

                setHasActiveBroadband(false);
                setActiveBroadbandPlan(null);
                setActivePlanChecking(false);

                return;
            }

            try {

                const response =
                    await axios.get(
                        `https://ritubroadband.in/api/getCurrentPlan.php?username=${encodeURIComponent(
                            username
                        )}`
                    );

                const responseData =
                    response.data;

                const isSuccess =
                    responseData?.success === true ||
                    String(
                        responseData?.status || ""
                    )
                        .trim()
                        .toLowerCase() === "success";

                const currentPlan =
                    isSuccess &&
                        responseData?.data
                        ? responseData.data
                        : null;


                const currentPlanType =
                    String(
                        currentPlan?.plan_type ||
                        ""
                    )
                        .trim()
                        .toUpperCase();

                const currentPlanStatus =
                    String(
                        currentPlan?.plan_status ||
                        currentPlan?.status ||
                        ""
                    )
                        .trim()
                        .toUpperCase();


                let active =
                    !!currentPlan &&
                    (
                        currentPlanType === "BROADBAND" ||
                        currentPlanType === ""
                    ) &&
                    currentPlanStatus === "ACTIVE";


                /*
                Check expiry date using the Asia/Kolkata
                parser defined above.
                */
                if (
                    active &&
                    currentPlan?.plan_expiry_date
                ) {

                    const expiry =
                        parseIndiaDateTime(
                            currentPlan.plan_expiry_date
                        );

                    if (
                        expiry &&
                        expiry.getTime() <= Date.now()
                    ) {
                        active = false;
                    }
                }


                setHasActiveBroadband(active);

                setActiveBroadbandPlan(
                    active
                        ? currentPlan
                        : null
                );

            } catch (error) {

                console.error(
                    "Active broadband validation error:",
                    error
                );

                /*
                For a direct add-on, failure to verify means
                we cannot safely allow the purchase.

                For a broadband purchase, the user can still
                proceed after the API check completes because
                there may simply be no active plan.
                */
                setHasActiveBroadband(false);
                setActiveBroadbandPlan(null);

            } finally {

                setActivePlanChecking(false);

            }

        };


        checkActiveBroadband();

    }, [
        username,
        state.hasActiveBroadband,
        state.activeBroadbandPlan
    ]);


    /*
    =========================================================
    VALIDATE PAYMENT DATA
    =========================================================
    */

    if (!plan || !duration) {

        return (
            <div className="payment-bg">

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar
                />

                <div className="no-plan-card">

                    <div className="no-plan-icon-wrapper">
                        <Wifi
                            size={40}
                            className="pulse-icon"
                        />
                    </div>

                    <h2>
                        No Plan Selected
                    </h2>

                    <p>
                        It seems you haven't selected a
                        subscription plan yet. Please go
                        back and select a plan.
                    </p>

                    <button
                        className="cancel-btn-premium"
                        onClick={() =>
                            navigate("/user")
                        }
                    >
                        Return to Dashboard
                    </button>

                </div>

            </div>
        );
    }


    /*
    =========================================================
    DIRECT ADD-ON ACTIVE PLAN CHECK
    =========================================================
    */

    if (
        isDirectAddonPurchase &&
        activePlanChecking
    ) {

        return (
            <div className="payment-bg">

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar
                />

                <div className="no-plan-card">

                    <div className="no-plan-icon-wrapper">

                        <Wifi
                            size={40}
                            className="pulse-icon"
                        />

                    </div>

                    <h2>
                        Checking Active Plan
                    </h2>

                    <p>
                        Please wait while we verify
                        your active broadband plan.
                    </p>

                </div>

            </div>
        );

    }


    /*
    =========================================================
    NO ACTIVE BROADBAND FOR DIRECT ADD-ON
    =========================================================
    */

    if (
        isDirectAddonPurchase &&
        !hasActiveBroadband
    ) {

        return (
            <div className="payment-bg">

                <ToastContainer
                    position="top-right"
                    autoClose={3500}
                    hideProgressBar
                />

                <div className="no-plan-card">

                    <div className="no-plan-icon-wrapper">

                        <Wifi
                            size={40}
                            className="pulse-icon"
                        />

                    </div>

                    <h2>
                        Active Broadband Plan Required
                    </h2>

                    <p>
                        You cannot purchase an OTT
                        add-on without an active
                        broadband plan.
                    </p>

                    <button
                        className="cancel-btn-premium"
                        onClick={() =>
                            navigate("/user")
                        }
                    >
                        Buy Broadband Plan
                    </button>

                </div>

            </div>
        );

    }


    /*
    =========================================================
    PAYMENT
    =========================================================
    */

    const processPayment = async () => {

        /*
        Direct ADDON validation
        */

        if (
            isDirectAddonPurchase &&
            !hasActiveBroadband
        ) {

            toast.warning(
                "There is no active broadband plan. Please buy an active broadband plan first."
            );

            return;
        }


        /*
        Validate amount
        */

        if (
            !totalPayable ||
            totalPayable <= 0
        ) {

            toast.error(
                "Invalid payment amount."
            );

            return;
        }


        try {

            setLoading(true);


            /*
            =================================================
            CREATE RAZORPAY ORDER
            =================================================
            */

            const orderResponse =
                await axios.post(
                    "https://ritubroadband.in/api/create_order.php",
                    {

                        /*
                        Total amount customer pays
                        */

                        amount:
                            totalPayable,


                        /*
                        User
                        */

                        username,


                        customer_name:
                            customerName,

                        email,

                        mobile,


                        /*
                        Plan type

                        BROADBAND
                        ADDON
                        */

                        plan_type:
                            planType,


                        /*
                        Broadband duration ID
                        */

                        broadband_plan_duration_id:
                            !isDirectAddonPurchase
                                ? (
                                    duration?.id ||
                                    duration?.duration_id ||
                                    null
                                )
                                : null,


                        /*
                        Add-on plan ID
                        */

                        addon_plan_id:
                            selectedAddon?.id ||
                            (
                                isDirectAddonPurchase
                                    ? plan?.id
                                    : null
                            ),


                        /*
                        Plan name
                        */

                        plan_name:
                            displayPlanName,


                        /*
                        Calculation values
                        */

                        service_subtotal:
                            serviceSubtotal,

                        service_gst:
                            serviceGst,

                        razorpay_fee:
                            razorpayFee,

                        razorpay_fee_gst:
                            razorpayFeeGst,

                        total_payable:
                            totalPayable

                    }
                );


            if (
                !orderResponse.data?.success
            ) {

                toast.error(
                    orderResponse.data?.message ||
                    "Unable to create payment order"
                );

                setLoading(false);

                return;
            }


            /*
            =================================================
            RAZORPAY OPTIONS
            =================================================
            */

            const options = {

                key:
                    "rzp_live_TYGfrghbs8e4Kl",

                amount:
                    orderResponse.data.amount,

                currency:
                    orderResponse.data.currency ||
                    "INR",

                name:
                    "Ritu Broad Band",

                description:
                    isDirectAddonPurchase
                        ? `OTT Add-on - ${addonPlanName}`
                        : hasAddonWithBroadband
                            ? `${displayPlanName} + OTT Add-on`
                            : `${displayPlanName} - ${broadbandDurationLabel}`,

                image:
                    logo,

                order_id:
                    orderResponse.data.order_id,

                prefill: {

                    name:
                        customerName,

                    email:
                        email,

                    contact:
                        mobile

                },

                theme: {
                    color:
                        "#0056d2"
                },


                /*
                =================================================
                RAZORPAY SUCCESS HANDLER
                =================================================
                */

                handler:
                    async function (
                        paymentResponse
                    ) {

                        try {

                            /*
                            =====================================
                            VERIFY PAYMENT
                            =====================================
                            */

                            const verifyResponse =
                                await axios.post(
                                    "https://ritubroadband.in/api/verify_payment.php",
                                    {

                                        /*
                                        Razorpay
                                        */

                                        razorpay_order_id:
                                            paymentResponse.razorpay_order_id,

                                        razorpay_payment_id:
                                            paymentResponse.razorpay_payment_id,

                                        razorpay_signature:
                                            paymentResponse.razorpay_signature,


                                        /*
                                        User
                                        */

                                        username,

                                        customer_name:
                                            customerName,

                                        email,

                                        mobile,


                                        /*
                                        =================================
                                        PLAN TYPE
                                        =================================
                                        */

                                        plan_type:
                                            planType,


                                        /*
                                        =================================
                                        BROADBAND PLAN
                                        =================================
                                        */

                                        plan_name:
                                            displayPlanName,

                                        broadband_plan_duration_id:
                                            !isDirectAddonPurchase
                                                ? (
                                                    duration?.id ||
                                                    duration?.duration_id ||
                                                    null
                                                )
                                                : null,


                                        /*
                                        =================================
                                        ADD-ON PLAN
                                        =================================
                                        */

                                        addon_plan_id:
                                            selectedAddon?.id ||
                                            (
                                                isDirectAddonPurchase
                                                    ? plan?.id
                                                    : null
                                            ),

                                        addon_plan_name:
                                            selectedAddon
                                                ? addonPlanName
                                                : null,

                                        addon_duration_months:
                                            selectedAddon
                                                ? (
                                                    selectedAddon.months ||
                                                    selectedAddon.duration_months ||
                                                    null
                                                )
                                                : null,

                                        addon_duration_label:
                                            selectedAddon
                                                ? addonDurationLabel
                                                : null,


                                        /*
                                        =================================
                                        BROADBAND DURATION
                                        =================================
                                        */

                                        duration_months:
                                            !isDirectAddonPurchase
                                                ? (
                                                    duration?.months ||
                                                    duration?.duration_months ||
                                                    1
                                                )
                                                : null,

                                        duration_label:
                                            !isDirectAddonPurchase
                                                ? broadbandDurationLabel
                                                : null,


                                        /*
                                        =================================
                                        CALCULATIONS
                                        =================================
                                        */

                                        amount:
                                            totalPayable,

                                        service_subtotal:
                                            serviceSubtotal,

                                        service_gst:
                                            serviceGst,

                                        razorpay_fee:
                                            razorpayFee,

                                        razorpay_fee_gst:
                                            razorpayFeeGst,

                                        total_payable:
                                            totalPayable

                                    }
                                );


                            setLoading(false);


                            /*
                            =====================================
                            SUCCESS
                            =====================================
                            */

                            if (
                                verifyResponse.data?.success
                            ) {

                                toast.success(
                                    "Payment Successful 🎉"
                                );


                                /*
                                Give backend a moment
                                to commit the data.
                                */

                                setTimeout(() => {

                                    navigate(
                                        "/user",
                                        {
                                            state: {
                                                paymentSuccess:
                                                    true,

                                                planType:
                                                    planType,

                                                addonPurchased:
                                                    !!selectedAddon
                                            }
                                        }
                                    );

                                }, 800);


                            } else {

                                toast.error(
                                    verifyResponse.data?.message ||
                                    "Payment verification failed"
                                );

                            }

                        } catch (error) {

                            console.error(
                                "Payment verification error:",
                                error
                            );

                            setLoading(false);

                            toast.error(
                                error?.response?.data?.message ||
                                "Payment verification failed"
                            );

                        }

                    }

            };


            /*
            =================================================
            CREATE RAZORPAY INSTANCE
            =================================================
            */

            if (
                !window.Razorpay
            ) {

                toast.error(
                    "Razorpay SDK is not loaded."
                );

                setLoading(false);

                return;
            }


            const razorpay =
                new window.Razorpay(
                    options
                );


            /*
            =================================================
            PAYMENT FAILED
            =================================================
            */

            razorpay.on(
                "payment.failed",
                function (response) {

                    setLoading(false);

                    console.error(
                        "Razorpay payment failed:",
                        response
                    );

                    toast.error(
                        response?.error?.description ||
                        "Payment failed"
                    );

                }
            );


            /*
            =================================================
            OPEN RAZORPAY
            =================================================
            */

            razorpay.open();


        } catch (error) {

            setLoading(false);

            console.error(
                "Payment creation error:",
                error
            );

            toast.error(
                error?.response?.data?.message ||
                "Unable to create payment."
            );

        }

    };


    /*
    =========================================================
    PAYMENT CONFIRMATION
    =========================================================

    If an active broadband plan already exists, do not open
    Razorpay immediately.

    Broadband purchase:
        Show confirmation that another broadband payment is
        being attempted.

    OTT add-on purchase:
        Show confirmation that the add-on is being purchased
        against the existing active broadband plan.

    The add-on wording is intentionally NOT "another
    broadband plan".
    =========================================================
    */

    const payNow = async () => {

        if (activePlanChecking) {

            toast.info(
                "Please wait while we verify your current broadband plan."
            );

            return;
        }


        /*
        Direct ADDON requires an active broadband plan.
        */
        if (
            isDirectAddonPurchase &&
            !hasActiveBroadband
        ) {

            toast.warning(
                "There is no active broadband plan. Please buy an active broadband plan first."
            );

            return;
        }


        /*
        If an active broadband plan exists, ask for
        confirmation before opening Razorpay.
        */
        if (
            hasActiveBroadband &&
            !showActivePlanConfirmation
        ) {

            setShowActivePlanConfirmation(true);

            return;
        }


        await processPayment();

    };


    /*
    =========================================================
    UI
    =========================================================
    */

    return (

        <div className="payment-bg">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar
            />


            {/* =====================================================
                ACTIVE PLAN CONFIRMATION MODAL
            ===================================================== */}

            {showActivePlanConfirmation && (

                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        background: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px"
                    }}
                >

                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="active-plan-confirmation-title"
                        style={{
                            width: "100%",
                            maxWidth: "480px",
                            background: "#ffffff",
                            borderRadius: "18px",
                            padding: "26px",
                            boxShadow: "0 24px 70px rgba(0,0,0,0.25)",
                            border: "1px solid #e2e8f0"
                        }}
                    >

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                marginBottom: "16px"
                            }}
                        >

                            <div
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    borderRadius: "50%",
                                    background: "#eff6ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                {isDirectAddonPurchase ? (
                                    <Tv
                                        size={22}
                                        color="#7c3aed"
                                    />
                                ) : (
                                    <Wifi
                                        size={22}
                                        color="#0056d2"
                                    />
                                )}
                            </div>

                            <div>

                                <h3
                                    id="active-plan-confirmation-title"
                                    style={{
                                        margin: 0,
                                        color: "#0f172a",
                                        fontSize: "19px",
                                        fontWeight: 700
                                    }}
                                >
                                    Active Plan Already Exists
                                </h3>

                                <p
                                    style={{
                                        margin: "4px 0 0",
                                        color: "#64748b",
                                        fontSize: "13px"
                                    }}
                                >
                                    Please confirm before continuing
                                </p>

                            </div>

                        </div>


                        {isDirectAddonPurchase ? (

                            <p
                                style={{
                                    color: "#334155",
                                    fontSize: "14px",
                                    lineHeight: 1.6,
                                    margin: "0 0 18px"
                                }}
                            >
                                You already have an active broadband
                                plan. You are purchasing the{" "}
                                <strong>
                                    {addonPlanName}
                                </strong>{" "}
                                OTT add-on. This payment will be
                                recorded separately from your broadband
                                subscription.
                            </p>

                        ) : (

                            <p
                                style={{
                                    color: "#334155",
                                    fontSize: "14px",
                                    lineHeight: 1.6,
                                    margin: "0 0 18px"
                                }}
                            >
                                You already have an active broadband
                                plan
                                {activeBroadbandPlan?.plan_name
                                    ? (
                                        <>
                                            {" "}
                                            (
                                            <strong>
                                                {
                                                    activeBroadbandPlan.plan_name
                                                }
                                            </strong>
                                            )
                                        </>
                                    )
                                    : ""}
                                . You are trying to make another
                                broadband payment. Do you want to
                                continue?
                            </p>

                        )}


                        <div
                            style={{
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                borderRadius: "12px",
                                padding: "13px 14px",
                                marginBottom: "20px"
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "15px",
                                    marginBottom: "7px"
                                }}
                            >

                                <span
                                    style={{
                                        color: "#64748b",
                                        fontSize: "13px"
                                    }}
                                >
                                    Payment
                                </span>

                                <strong
                                    style={{
                                        color: "#0f172a",
                                        fontSize: "14px"
                                    }}
                                >
                                    ₹{totalPayable.toFixed(2)}
                                </strong>

                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "15px"
                                }}
                            >

                                <span
                                    style={{
                                        color: "#64748b",
                                        fontSize: "13px"
                                    }}
                                >
                                    Purchase
                                </span>

                                <strong
                                    style={{
                                        color: "#0f172a",
                                        fontSize: "14px",
                                        textAlign: "right"
                                    }}
                                >
                                    {displayPlanName}
                                </strong>

                            </div>

                        </div>


                        <div
                            style={{
                                display: "flex",
                                gap: "10px"
                            }}
                        >

                            <button
                                type="button"
                                onClick={() =>
                                    setShowActivePlanConfirmation(false)
                                }
                                style={{
                                    flex: 1,
                                    padding: "12px 16px",
                                    borderRadius: "10px",
                                    border: "1px solid #cbd5e1",
                                    background: "#ffffff",
                                    color: "#334155",
                                    fontWeight: 600,
                                    cursor: "pointer"
                                }}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"
                                onClick={() => {

                                    setShowActivePlanConfirmation(
                                        false
                                    );

                                    processPayment();

                                }}
                                style={{
                                    flex: 1,
                                    padding: "12px 16px",
                                    borderRadius: "10px",
                                    border: "none",
                                    background: "#0056d2",
                                    color: "#ffffff",
                                    fontWeight: 600,
                                    cursor: "pointer"
                                }}
                            >
                                Continue to Payment
                            </button>

                        </div>

                    </div>

                </div>

            )}


            <div className="payment-container">


                {/* =====================================================
                    LEFT SIDE
                ===================================================== */}

                <div className="payment-main">


                    <div className="checkout-header">

                        <button
                            className="back-link-btn"
                            onClick={() =>
                                navigate(-1)
                            }
                            title="Go Back"
                        >

                            <ArrowLeft size={18} />

                            <span>
                                Go Back
                            </span>

                        </button>


                        <div className="secure-badge">

                            <ShieldCheck
                                size={16}
                                className="secure-icon"
                            />

                            <span>
                                256-Bit SSL Secured
                            </span>

                        </div>

                    </div>


                    {/* =================================================
                        BRAND
                    ================================================= */}

                    <div className="brand-section">

                        <img
                            src={logo}
                            alt="Ritu Broadband"
                            className="payment-logo"
                        />


                        <div className="brand-info">

                            <h2>
                                Ritu Broad Band
                            </h2>

                            <p>
                                Complete your subscription payment
                                securely via Razorpay
                            </p>

                        </div>

                    </div>


                    {/* =================================================
                        CUSTOMER DETAILS
                    ================================================= */}

                    <div className="payment-section-title">

                        <h3>
                            Customer Billing Info
                        </h3>

                        <p>
                            Please verify your billing and account
                            details before proceeding
                        </p>

                    </div>


                    <div className="customer-details-grid">


                        <div className="detail-card">

                            <div className="detail-icon-wrapper">
                                <User size={18} />
                            </div>

                            <div className="detail-info">

                                <span className="detail-label">
                                    Full Name
                                </span>

                                <span className="detail-value">
                                    {customerName || "N/A"}
                                </span>

                            </div>

                        </div>


                        <div className="detail-card">

                            <div className="detail-icon-wrapper">
                                <FileText size={18} />
                            </div>

                            <div className="detail-info">

                                <span className="detail-label">
                                    Username
                                </span>

                                <span className="detail-value">
                                    {username || "N/A"}
                                </span>

                            </div>

                        </div>


                        <div className="detail-card">

                            <div className="detail-icon-wrapper">
                                <Phone size={18} />
                            </div>

                            <div className="detail-info">

                                <span className="detail-label">
                                    Mobile Number
                                </span>

                                <span className="detail-value">
                                    {mobile || "N/A"}
                                </span>

                            </div>

                        </div>


                        <div className="detail-card">

                            <div className="detail-icon-wrapper">
                                <Mail size={18} />
                            </div>

                            <div className="detail-info">

                                <span className="detail-label">
                                    Email Address
                                </span>

                                <span className="detail-value">
                                    {email || "N/A"}
                                </span>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        PURCHASE TYPE
                    ================================================= */}

                    <div
                        style={{
                            marginTop: "22px",
                            padding: "16px",
                            borderRadius: "12px",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0"
                        }}
                    >

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px"
                            }}
                        >

                            {isDirectAddonPurchase ? (
                                <Tv
                                    size={20}
                                    color="#0056d2"
                                />
                            ) : (
                                <Wifi
                                    size={20}
                                    color="#0056d2"
                                />
                            )}

                            <strong>
                                {isDirectAddonPurchase
                                    ? "OTT Add-on Purchase"
                                    : hasAddonWithBroadband
                                        ? "Broadband + OTT Add-on"
                                        : "Broadband Subscription"}
                            </strong>

                        </div>


                        {!isDirectAddonPurchase &&
                            hasAddonWithBroadband && (

                                <p
                                    style={{
                                        margin:
                                            "10px 0 0 30px",
                                        color:
                                            "#64748b",
                                        fontSize:
                                            "14px"
                                    }}
                                >
                                    Your broadband plan and
                                    OTT add-on will remain
                                    separate subscriptions,
                                    even though they are paid
                                    in one transaction.
                                </p>

                            )}

                    </div>


                    {/* =================================================
                        TERMS + PAY BUTTON
                    ================================================= */}

                    <div className="payment-action-box">

                        <p
                            style={{
                                fontSize: "0.78rem",
                                color: "#64748b",
                                textAlign: "center",
                                margin: "0 0 12px",
                                lineHeight: "1.5"
                            }}
                        >

                            By continuing, you agree to our{" "}

                            <a
                                href="/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: "#0B72B9",
                                    fontWeight: "600",
                                    textDecoration:
                                        "underline"
                                }}
                            >
                                Terms &amp; Conditions
                            </a>

                            . Payments are processed
                            securely via Razorpay.

                        </p>


                        <button
                            className="pay-btn-premium"
                            onClick={payNow}
                            disabled={
                                loading ||
                                activePlanChecking ||
                                (
                                    isDirectAddonPurchase &&
                                    !hasActiveBroadband
                                )
                            }
                        >

                            {loading ? (

                                <span className="loading-spinner-container">

                                    <span className="spinner-dot"></span>

                                    Processing Securely...

                                </span>

                            ) : (

                                <>

                                    <Lock size={18} />

                                    <span>
                                        Pay Securely ₹
                                        {totalPayable.toFixed(2)}
                                    </span>

                                </>

                            )}

                        </button>


                        <button
                            className="cancel-btn-link"
                            onClick={() =>
                                navigate("/user")
                            }
                        >
                            Cancel Payment &amp; Return
                        </button>

                    </div>


                    {/* =================================================
                        TRUST FOOTER
                    ================================================= */}

                    <div className="trust-footer">

                        <div className="trust-badge">

                            <CreditCard size={14} />

                            <span>
                                Razorpay Gateway
                            </span>

                        </div>


                        <div className="trust-divider"></div>


                        <div className="trust-badge">

                            <ShieldCheck size={14} />

                            <span>
                                PCI-DSS Secured
                            </span>

                        </div>

                    </div>

                </div>


                {/* =====================================================
                    MOBILE STICKY PAY BAR
                    Shown only on ≤768px via CSS (display:none on desktop)
                ===================================================== */}

                <div className="mobile-pay-sticky-bar">

                    <p className="mobile-pay-terms">
                        By continuing, you agree to our{" "}
                        <a
                            href="/terms-and-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms &amp; Conditions
                        </a>
                        . Payments are processed securely via Razorpay.
                    </p>

                    <button
                        className="mobile-pay-btn"
                        onClick={payNow}
                        disabled={
                            loading ||
                            activePlanChecking ||
                            (isDirectAddonPurchase && !hasActiveBroadband)
                        }
                    >
                        {loading ? (
                            <span className="loading-spinner-container">
                                <span className="spinner-dot"></span>
                                Processing Securely...
                            </span>
                        ) : (
                            <>
                                <Lock size={18} />
                                <span>Pay Securely ₹{totalPayable.toFixed(2)}</span>
                            </>
                        )}
                    </button>

                    <button
                        className="mobile-cancel-link"
                        onClick={() => navigate("/user")}
                    >
                        Cancel Payment &amp; Return
                    </button>

                </div>


                {/* =====================================================
                    RIGHT SIDE - ORDER SUMMARY
                ===================================================== */}

                <div className="payment-summary">


                    <div className="summary-header">

                        <h3>
                            Order Summary
                        </h3>

                        <span className="receipt-badge">
                            Invoice Summary
                        </span>

                    </div>


                    {/* =================================================
                        BROADBAND PLAN
                    ================================================= */}

                    {!isDirectAddonPurchase && (

                        <div className="summary-plan-card">

                            <div className="summary-plan-main">

                                <div className="plan-icon-globe">

                                    <Wifi
                                        size={24}
                                        className="wifi-glow-icon"
                                    />

                                </div>


                                <div className="plan-meta">

                                    <span className="plan-tag">
                                        Broadband Subscription
                                    </span>

                                    <h4 className="plan-speed-title">
                                        {displayPlanName}
                                    </h4>

                                    <small>
                                        {broadbandDurationLabel}
                                    </small>

                                </div>

                            </div>

                            <div className="plan-highlight-bar"></div>

                        </div>

                    )}


                    {/* =================================================
                        ADD-ON PLAN
                    ================================================= */}

                    {selectedAddon && (

                        <div
                            className="summary-plan-card"
                            style={{
                                marginTop: "14px"
                            }}
                        >

                            <div className="summary-plan-main">

                                <div className="plan-icon-globe">

                                    <Tv
                                        size={24}
                                        color="#7c3aed"
                                    />

                                </div>


                                <div className="plan-meta">

                                    <span className="plan-tag">
                                        OTT Add-on
                                    </span>

                                    <h4 className="plan-speed-title">

                                        {addonPlanName}

                                    </h4>

                                    <small>
                                        {addonDurationLabel}
                                    </small>

                                </div>

                            </div>

                            <div
                                className="plan-highlight-bar"
                                style={{
                                    background:
                                        "linear-gradient(90deg,#7c3aed,#a855f7)"
                                }}
                            ></div>

                        </div>

                    )}


                    {/* =================================================
                        RECEIPT BREAKDOWN
                    ================================================= */}

                    <div className="receipt-breakdown">


                        {/* Broadband amount */}

                        {!isDirectAddonPurchase && (

                            <div className="receipt-row">

                                <span className="receipt-label">
                                    Broadband Plan
                                </span>

                                <span className="receipt-value">
                                    ₹
                                    {broadbandAmount.toFixed(2)}
                                </span>

                            </div>

                        )}


                        {/* Add-on amount */}

                        {selectedAddon && (

                            <div className="receipt-row">

                                <span className="receipt-label">
                                    OTT Add-on
                                </span>

                                <span className="receipt-value">
                                    ₹
                                    {addonAmount.toFixed(2)}
                                </span>

                            </div>

                        )}


                        {/* Subtotal */}

                        <div className="receipt-row">

                            <span className="receipt-label">
                                Service Subtotal
                            </span>

                            <span className="receipt-value">
                                ₹
                                {serviceSubtotal.toFixed(2)}
                            </span>

                        </div>

                        {/* Razorpay */}

                        <div className="receipt-row">

                            <span className="receipt-label">
                                Razorpay Platform Fee (2%)
                            </span>

                            <span className="receipt-value">
                                ₹
                                {razorpayFee.toFixed(2)}
                            </span>

                        </div>


                        {/* Razorpay GST */}

                        <div className="receipt-row">

                            <span className="receipt-label">
                                GST on Platform Fee (18%)
                            </span>

                            <span className="receipt-value">
                                ₹
                                {razorpayFeeGst.toFixed(2)}
                            </span>

                        </div>


                        <div className="receipt-divider-dashed"></div>


                        {/* Total */}

                        <div className="receipt-row total-row">

                            <span className="total-label">
                                Total Amount
                            </span>

                            <span className="total-price">
                                ₹
                                {totalPayable.toFixed(2)}
                            </span>

                        </div>

                    </div>


                    {/* =================================================
                        BENEFITS
                    ================================================= */}

                    <div className="secure-benefits-card">

                        <h5>

                            {isDirectAddonPurchase
                                ? "OTT Add-on Benefits"
                                : hasAddonWithBroadband
                                    ? "Subscription Benefits"
                                    : "Broadband Benefits"}

                        </h5>


                        <ul className="benefits-list">


                            {/* Broadband features */}

                            {!isDirectAddonPurchase &&
                                plan?.features &&
                                plan.features.length > 0 && (

                                    plan.features
                                        .slice(0, 4)
                                        .map(
                                            (
                                                feature,
                                                index
                                            ) => (

                                                <li
                                                    key={
                                                        feature.id ||
                                                        index
                                                    }
                                                >

                                                    <div className="benefit-dot"></div>

                                                    <span>
                                                        {
                                                            feature.feature_text
                                                        }
                                                    </span>

                                                </li>

                                            )
                                        )

                                )}


                            {/* Add-on features */}

                            {selectedAddon?.features &&
                                selectedAddon.features
                                    .length > 0 && (

                                    selectedAddon.features
                                        .slice(0, 4)
                                        .map(
                                            (
                                                feature,
                                                index
                                            ) => (

                                                <li
                                                    key={
                                                        feature.id ||
                                                        index
                                                    }
                                                >

                                                    <div className="benefit-dot"></div>

                                                    <span>
                                                        {
                                                            feature.feature_text
                                                        }
                                                    </span>

                                                </li>

                                            )
                                        )

                                )}


                            {/* Default */}

                            {!plan?.features?.length &&
                                !selectedAddon?.features?.length && (

                                    <>

                                        <li>

                                            <div className="benefit-dot"></div>

                                            <span>
                                                Unlimited
                                                high-speed
                                                internet
                                            </span>

                                        </li>

                                        <li>

                                            <div className="benefit-dot"></div>

                                            <span>
                                                Fast and
                                                reliable
                                                connectivity
                                            </span>

                                        </li>

                                        <li>

                                            <div className="benefit-dot"></div>

                                            <span>
                                                Customer
                                                support
                                            </span>

                                        </li>

                                    </>

                                )}

                        </ul>

                    </div>


                </div>

            </div>

        </div>
    );
}

export default PaymentPage;